import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Code,
    title: "Custom Website Development",
    description:
      "Custom websites built around your brand, offer, and business goals.",
  },
  {
    icon: Smartphone,
    title: "Responsive Website Design",
    description:
      "Mobile-friendly layouts that work smoothly across devices and screen sizes.",
  },
  {
    icon: Zap,
    title: "Performance Optimization",
    description:
      "Fast-loading pages built for better usability and stronger results.",
  },
  {
    icon: Shield,
    title: "Security-First Development",
    description:
      "Clean builds with reliable structure, SSL setup, and secure implementation practices.",
  },
  {
    icon: TrendingUp,
    title: "SEO Integration",
    description:
      "On-page SEO foundations built into the website from the start.",
  },
  {
    icon: Layers,
    title: "CMS or Admin Access",
    description:
      "Content management access or custom admin functionality based on what the business needs.",
  },
];

const offerings = [
  {
    title: "Corporate Websites",
    description:
      "Professional websites for service businesses that need credibility, clarity, and a stronger online presence.",
  },
  {
    title: "Landing Pages",
    description:
      "Focused pages built for campaigns, offers, and clear conversion goals.",
  },
  {
    title: "Web Applications",
    description:
      "Custom digital products for businesses that need more than a standard website.",
  },
  {
    title: "Portal Development",
    description:
      "Secure internal or client-facing portals that improve workflows and control.",
  },
];

const deliverables = [
  "Fully responsive design",
  "SEO-friendly page structure",
  "Clean and scalable build",
  "Core performance optimization",
  "Security and SSL setup",
  "CMS or admin access where needed",
  "Analytics and tracking setup",
  "Post-launch support guidance",
];

const processSteps = [
  {
    step: "01",
    title: "Discovery",
    description:
      "We learn about your business, audience, goals, and current website challenges.",
  },
  {
    step: "02",
    title: "Planning",
    description:
      "We define the sitemap, content flow, SEO priorities, and the right structure for the site.",
  },
  {
    step: "03",
    title: "Design",
    description:
      "We create layouts that support clarity, usability, and the next step you want users to take.",
  },
  {
    step: "04",
    title: "Development",
    description:
      "We build the website with performance, functionality, and SEO in mind.",
  },
  {
    step: "05",
    title: "Testing",
    description:
      "We review speed, responsiveness, structure, and overall user experience before launch.",
  },
  {
    step: "06",
    title: "Launch & Support",
    description:
      "We launch cleanly, then support updates and ongoing improvements as needed.",
  },
];

const faqs = [
  {
    question: "How long does it take to build a website?",
    answer: (
      <>
        The timeline depends on the size, functionality, and content
        requirements of the project. After discovery, we provide a clear scope
        and timeline.
      </>
    ),
    schemaAnswer:
      "The timeline depends on the size, functionality, and content requirements of the project. After discovery, we provide a clear scope and timeline.",
  },
  {
    question: "What platforms do you develop on?",
    answer: (
      <>
        We work with WordPress, custom CMS setups, and custom-built solutions
        depending on the needs of the project.
      </>
    ),
    schemaAnswer:
      "We work with WordPress, custom CMS setups, and custom-built solutions depending on the needs of the project.",
  },
  {
    question: "Will my website be SEO-friendly?",
    answer: (
      <>
        Yes. We build websites with{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          SEO-friendly
        </Link>{" "}
        structure, clean code, strong performance, and technical foundations
        that support{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          visibility on Google
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "Yes. We build websites with SEO-friendly structure, clean code, strong performance, and technical foundations that support visibility on Google.",
  },
  {
    question: "Can I update the website myself?",
    answer: (
      <>
        Yes. If your project includes a{" "}
        <Link
          to="/services/custom-tool-creation"
          className="text-primary hover:underline"
        >
          CMS or admin area
        </Link>
        , your team will be able to manage content more easily.
      </>
    ),
    schemaAnswer:
      "Yes. If your project includes a CMS or admin area, your team will be able to manage content more easily.",
  },
  {
    question: "Do you also improve existing websites?",
    answer: (
      <>
        Yes. We can improve structure, usability, SEO, and conversion
        performance on existing websites.
      </>
    ),
    schemaAnswer:
      "Yes. We can improve structure, usability, SEO, and conversion performance on existing websites.",
  },
  {
    question: "What happens after launch?",
    answer: (
      <>
        After launch, we can continue with{" "}
        <Link
          to="/services/maintenance-support"
          className="text-primary hover:underline"
        >
          maintenance, support
        </Link>
        , SEO improvements, and ongoing updates based on your goals.
      </>
    ),
    schemaAnswer:
      "After launch, we can continue with maintenance, support, SEO improvements, and ongoing updates based on your goals.",
  },
];

const whyChooseUs = [
  {
    title: "Built Around Your Business",
    description:
      "We shape the website around your goals, your service flow, and what your users need to do next.",
  },
  {
    title: "Strategy Before Design",
    description:
      "We define the structure and purpose first so the design supports the business, not just the layout.",
  },
  {
    title: "SEO-Friendly Foundations",
    description:
      "We build websites with clean structure, performance, and search visibility in mind from day one.",
  },
  {
    title: "Support Beyond Launch",
    description:
      "We continue supporting updates, fixes, and improvements after the website goes live.",
  },
];

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
     <SEO
  title="Top website development company in vancouver | Square Trigger"
  description="Best website design and development company in Vancouver. We design high-performance websites for small to mid-sized businesses in Canada. Read more."
  ogTitle="Top website development company in vancouver | Square Trigger"
  ogDescription="Best website design and development company in Vancouver. We design high-performance websites for small to mid-sized businesses in Canada. Read more."
  ogSiteName="Square Trigger website design and development"
  ogImage="https://squaretrigger.com/website1.webp"
  keywords={SEO_KEYWORDS.websiteDevelopment}
/>

     {/* Hero Section */}
<section className="relative overflow-hidden">
 
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
    style={{ backgroundImage: "url('/website1.webp')" }}
  />

  {/* Overlay for mobile + desktop */}
  <div className="absolute inset-0 bg-gradient-to-r from-foreground/72 via-foreground/58 to-foreground/38 lg:bg-gradient-to-br lg:from-foreground lg:via-foreground/95 lg:to-foreground/90" />

  <div className="absolute inset-0">
          <div className="absolute top-16 right-6 h-72 w-72 rounded-full bg-primary/20 blur-3xl sm:top-20 sm:right-20 sm:h-96 sm:w-96" />
          <div className="absolute bottom-16 left-6 h-56 w-56 rounded-full bg-secondary/20 blur-3xl sm:bottom-20 sm:left-20 sm:h-72 sm:w-72" />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">Our Services</span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Website Development Company in{" "}
                <span className="gradient-text">Vancouver</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-3 max-w-[640px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We build custom websites that are fast, clear, SEO-ready, and built
                to help your business generate better enquiries and stronger first
                impressions.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mx-auto m-0 mb-6 max-w-[640px] text-[0.95rem] italic text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We create websites with clear structure, better performance, and a
                stronger user experience so your business looks professional from the
                first click.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link
                    to="/work"
                    className="flex items-center justify-center gap-2"
                  >
                    View Our Work
                  </Link>
                </Button>
              </motion.div>
            </div>

            <div className="hidden min-h-[420px] items-center justify-center lg:flex">
  <motion.div
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, delay: 0.4 }}
    className="relative flex items-center justify-center"
  >
    
    <div className="relative group">
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
      <img 
        src="/website1.webp" 
        alt="Digital Agency Vancouver" 
        className="relative rounded-2xl shadow-2xl border border-white/10 object-cover w-full max-w-[520px] h-auto"
      />
    </div>
  </motion.div>
</div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="mb-4 text-3xl font-display font-bold text-foreground md:text-4xl">
              Your Website Is Your{" "}
              <span className="gradient-text">Digital Foundation</span>
            </h2>

            <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              Your website is often the first real interaction someone has with
              your business. If it feels outdated, unclear, or difficult to use,
              trust drops quickly and opportunities are lost before a
              conversation even starts.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8"
            >
              <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                As a website development company in Vancouver, we build websites
                around what matters most: clarity, performance, usability, and a
                structure that helps visitors take the next step.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
                {[
                  {
                    title: "Clear messaging",
                    desc: "Visitors should understand what you do within seconds.",
                  },
                  {
                    title: "Strong structure",
                    desc: "Every page should guide users naturally toward the next action.",
                  },
                  {
                    title: "SEO-ready build",
                    desc: "A strong website should support search visibility from the start.",
                  },
                  {
                    title: "Conversion focus",
                    desc: "Good design should also help your business generate more enquiries.",
                  },
                  {
                    title: "Built to grow",
                    desc: "Your website should support where the business is going, not just where it is today.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center transition-shadow hover:shadow-medium"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {item.desc}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <p className="mx-auto mt-2 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                  A strong website should be{" "}
                  <Link
                    to="/services/seo-services"
                    className="text-primary hover:underline"
                  >
                    SEO-ready
                  </Link>{" "}
                  from the start and designed to{" "}
                  <Link
                    to="/contact"
                    className="text-primary hover:underline"
                  >
                    generate more enquiries
                  </Link>
                  .
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-14 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
              Why Choose Square Trigger for Website Development
            </h2>
            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              We do not treat website projects like template work. We start by
              understanding the business, the audience, and what the website
              actually needs to achieve.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
              >
                <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary-soft sm:mx-0">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="mb-2 font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {item.title === "SEO-Friendly Foundations" ? (
                    <>
                      We build websites with clean structure, performance, and{" "}
                      <Link
                        to="/services/seo-services"
                        className="text-primary hover:underline"
                      >
                        search visibility
                      </Link>{" "}
                      in mind from day one.
                    </>
                  ) : item.title === "Support Beyond Launch" ? (
                    <>
                      We continue supporting{" "}
                      <Link
                        to="/services/maintenance-support"
                        className="text-primary hover:underline"
                      >
                        updates, fixes, and improvements
                      </Link>{" "}
                      after the website goes live.
                    </>
                  ) : (
                    item.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-14"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Capabilities
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              What We <span className="gradient-text">Deliver</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:items-start sm:text-left">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                      {capability.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {capability.title === "SEO Integration" ? (
                        <>
                          On-page{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            SEO
                          </Link>{" "}
                          foundations built into the website from the start.
                        </>
                      ) : capability.title === "CMS or Admin Access" ? (
                        <>
                          Content management access or{" "}
                          <Link
                            to="/services/custom-tool-creation"
                            className="text-primary hover:underline"
                          >
                            custom admin functionality
                          </Link>{" "}
                          based on what the business needs.
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              Solutions
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              What We <span className="gradient-text">Offer</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:p-8 sm:text-left"
              >
                <h3 className="mb-3 text-xl font-display font-bold sm:text-2xl">
                  {offering.title === "Web Applications" ? (
                    <Link
                      to="/services/custom-tool-creation"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : offering.title === "Portal Development" ? (
                    <Link
                      to="/services/custom-tool-creation"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="leading-relaxed text-background/70">
                  {offering.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium uppercase tracking-wider text-primary">
                Deliverables
              </span>

              <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="mx-auto mb-8 max-w-[720px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
                Every website development project includes the essentials needed
                for launch, usability, and long-term performance.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft sm:p-8 sm:text-left"
            >
              <ul className="inline-grid space-y-4 text-left sm:grid">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex w-fit items-start gap-3"
                  >
                    <CheckCircle className="mt-[2px] h-5 w-5 flex-shrink-0 text-primary" />
                    <span className="text-sm leading-relaxed text-foreground/90 sm:text-base">
                      {item === "SEO-friendly page structure" ? (
                        <>
                          SEO-friendly
                          page structure
                        </>
                      ) : item === "Post-launch support guidance" ? (
                        <>
                          Post-launch support
                          guidance
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-12 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Our Process
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              How We <span className="gradient-text">Build Your Website</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:text-left">
                  <div className="mb-4 flex w-full justify-center">
                    <div className="text-center text-3xl font-display font-bold gradient-text">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {step.title === "Planning" ? (
                      <>
                        We define the sitemap, content flow,{" "}
                        <Link
                          to="/services/seo-services"
                          className="text-primary hover:underline"
                        >
                          SEO priorities
                        </Link>
                        , and the right structure for the site.
                      </>
                    ) : step.title === "Launch & Support" ? (
                      <>
                        We launch cleanly, then support updates and{" "}
                        <Link
                          to="/services/maintenance-support"
                          className="text-primary hover:underline"
                        >
                          ongoing improvements
                        </Link>{" "}
                        as needed.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     {/* Mid CTA */}
<section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
        <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1000px]">
        <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
          Need a Website That Works Harder for Your Business?
        </h2>

        <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
          Let’s build a website that improves clarity, strengthens trust,
          and supports measurable growth.
        </p>

        <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
          <Button
            variant="premium"
            size="lg"
            asChild
            className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2"
            >
              Book a Free Consultation
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>

          <Button
            variant="glass"
            size="lg"
            asChild
            className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
          >
            <Link to="/work">View Our Work</Link>
          </Button>
        </div>
      </div>
    </motion.div>
  </div>
</section>

      {/* FAQs */}
<section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
  <div className="container-custom">
    <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-10 text-center sm:mb-12"
      >
        <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
          Website Development –{" "}
          <span className="gradient-text">Frequently Asked Questions</span>
        </h2>
      </motion.div>

      <div className="mx-auto max-w-[1000px] space-y-4">
        {faqs.map((faq, index) => (
          <motion.div
            key={faq.question}
            className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
          >
            <button
              type="button"
              onPointerUp={() => setOpenFaq(openFaq === index ? null : index)}
              className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
            >
              <span className="pr-6 font-semibold text-foreground">
                {faq.question}
              </span>
              <ChevronDown
                className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                  openFaq === index ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
            >
              <div className="overflow-hidden">
                <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                  {faq.answer}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.schemaAnswer,
            },
          })),
        })}
      </script>
    </div>
  </div>
</section>

      {/* Final CTA */}
<section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
    >
      <div className="mx-auto max-w-[1000px]">
        <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
          Ready to Build a Better Website for Your Business?
        </h2>

        <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
          Let’s talk about what your website needs to do, what may be
          holding it back, and how we can improve it.
        </p>

        <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
          <Button
            variant="gradient"
            size="lg"
            asChild
            className="group w-full justify-center sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2"
            >
              Book a Free Consultation
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>

          <Button
            variant="outline"
            size="lg"
            asChild
            className="w-full justify-center bg-background sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center"
            >
              Contact Us
            </Link>
          </Button>
        </div>
      </div>
    </motion.div>
  </div>
</section>
    </Layout>
  );
}